import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Form,
  InputGroup,
  ButtonGroup,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkedAlt,
  faGlobeAmericas,
  faFileExcel,
  faCalendarAlt,
  faChartBar,
  faUsers,
  faCity,
  faFilter,
  faRedoAlt,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Cell,
} from 'recharts';
import moment from 'moment';

import clientAxios from '../../clients/clientAxios';
import LoadingScreen from '../../Layout/LoadingScreen';
import CitiesExcel from './CitiesExcel';
import { filterAndformatDataForChart } from '../../helpers/filterByDate';

const MODERN_CHART_COLORS = [
  '#4F46E5',
  '#06B6D4',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#3B82F6',
  '#14B8A6',
  '#F97316',
  '#6366F1',
  '#84CC16',
  '#0EA5E9',
  '#A855F7',
  '#D946EF',
  '#E11D48',
  '#64748B',
  '#22C55E',
  '#EAB308',
  '#38BDF8',
];

const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length > 0) {
    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '14px 18px',
          boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.12), 0 6px 12px -3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0',
          minWidth: '180px',
        }}
      >
        <p
          style={{
            margin: 0,
            fontWeight: 700,
            color: '#1e293b',
            fontSize: '14px',
            marginBottom: '8px',
            borderBottom: '1px solid #f1f5f9',
            paddingBottom: '6px',
          }}
        >
          {label}
        </p>
        {payload.map((entryItem, itemIndex) => (
          <div
            key={`tooltip-item-${itemIndex}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              fontSize: '13px',
              margin: '4px 0',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#64748b',
              }}
            >
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: entryItem.color || entryItem.fill || '#4F46E5',
                  display: 'inline-block',
                }}
              />
              {entryItem.name || 'Estudiantes'}:
            </span>
            <span style={{ fontWeight: 700, color: '#0f172a' }}>
              {entryItem.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Cities() {
  const [stackedCountryData, setStackedCountryData] = useState([]);
  const [argentinaProvincesData, setArgentinaProvincesData] = useState([]);
  const [regionCategories, setRegionCategories] = useState([]);
  const [dataRaw, setDataRaw] = useState(null);
  const [countriesArray, setCountries] = useState([]);
  const [regionsArray, setRegions] = useState([]);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedViewMode, setSelectedViewMode] = useState('onlyCities');
  const [displayLimit, setDisplayLimit] = useState('top10');

  const [isPeriodChecked, setIsPeriodChecked] = useState(false);
  const [argentinaData, setArgentinaData] = useState(null);
  const [argentinaRawData, setArgentinaRawData] = useState(null);
  const [selectedInitialDate, setSelectedInitialDate] = useState(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [selectedFinalDate, setSelectedFinalDate] = useState(
    moment().endOf('month').format('YYYY-MM-DD')
  );

  const fetchCitiesData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await clientAxios.get('/graph/cities/');
      const responseData = response.data;
      setDataRaw(responseData);

      const collectedCountries = [];
      const collectedRegions = [];
      const formattedStackedData = [];
      const formattedArgentinaProvinces = [];

      const studentsByCountry = responseData.studentsOfSpecificCountry || {};

      for (const countryName in studentsByCountry) {
        if (countryName === 'Indefinido') {
          const undefinedList = studentsByCountry[countryName] || [];
          formattedStackedData.push({
            country: countryName,
            'Region no definida': undefinedList.length,
          });
        } else {
          collectedCountries.push(countryName);
          const countryObject = {
            country: countryName,
          };

          for (const regionName in studentsByCountry[countryName]) {
            collectedRegions.push(regionName);
            const studentCount =
              studentsByCountry[countryName][regionName]?.length || 0;
            countryObject[regionName] = studentCount;

            if (countryName === 'Argentina') {
              formattedArgentinaProvinces.push({
                name: regionName,
                value: studentCount,
              });
            }
          }

          if (countryName === 'Argentina') {
            setArgentinaData(studentsByCountry[countryName]);
            setArgentinaRawData(formattedArgentinaProvinces);
          }

          formattedStackedData.push(countryObject);
        }
      }

      setStackedCountryData(formattedStackedData);
      setArgentinaProvincesData(formattedArgentinaProvinces);
      setRegionCategories(responseData.regionText || []);
      setRegions(collectedRegions);
      setCountries(collectedCountries);
    } catch (fetchError) {
      console.error('Error al obtener estadísticas de ciudades:', fetchError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCitiesData();
  }, [fetchCitiesData]);

  useEffect(() => {
    if (isPeriodChecked && selectedInitialDate && selectedFinalDate && argentinaData) {
      const filteredResults = filterAndformatDataForChart(
        argentinaData,
        selectedInitialDate,
        selectedFinalDate
      );
      setArgentinaProvincesData(filteredResults);
    } else if (argentinaRawData) {
      setArgentinaProvincesData(argentinaRawData);
    }
  }, [selectedFinalDate, selectedInitialDate, isPeriodChecked, argentinaData, argentinaRawData]);

  const handlePeriodToggle = () => {
    const nextState = !isPeriodChecked;
    setIsPeriodChecked(nextState);
    if (nextState) {
      setSelectedViewMode('onlyCities');
    }
  };

  const handleResetFilters = () => {
    setIsPeriodChecked(false);
    setSelectedInitialDate(moment().startOf('month').format('YYYY-MM-DD'));
    setSelectedFinalDate(moment().endOf('month').format('YYYY-MM-DD'));
    if (argentinaRawData) {
      setArgentinaProvincesData(argentinaRawData);
    }
  };

  const sortedProvinces = useMemo(() => {
    const provinceList = [...argentinaProvincesData];
    provinceList.sort((itemA, itemB) => itemB.value - itemA.value);
    if (displayLimit === 'top10') {
      return provinceList.slice(0, 10);
    }
    return provinceList;
  }, [argentinaProvincesData, displayLimit]);

  const totalStudentsInArgentina = useMemo(() => {
    return argentinaProvincesData.reduce(
      (runningTotal, provinceItem) => runningTotal + (provinceItem.value || 0),
      0
    );
  }, [argentinaProvincesData]);

  const totalProvincesActive = useMemo(() => {
    return argentinaProvincesData.filter(
      (provinceItem) => (provinceItem.value || 0) > 0
    ).length;
  }, [argentinaProvincesData]);

  const topProvinceSummary = useMemo(() => {
    if (!argentinaProvincesData || argentinaProvincesData.length === 0) {
      return { name: '-', value: 0 };
    }
    const sortedList = [...argentinaProvincesData].sort(
      (itemA, itemB) => itemB.value - itemA.value
    );
    return sortedList[0] || { name: '-', value: 0 };
  }, [argentinaProvincesData]);

  const totalCountriesCount = useMemo(() => {
    return countriesArray.length;
  }, [countriesArray]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Container fluid className="px-0 pb-5">
      <CitiesExcel
        show={showExcelModal}
        onHide={() => setShowExcelModal(false)}
        dataRaw={dataRaw}
        countriesArray={countriesArray}
        regionsArray={regionsArray}
      />

      {/* Header & Action Bar */}
      <Row className="mb-4">
        <Col xs={12}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-white p-4 rounded-lg shadow-sm border mb-3">
            <div>
              <h4 className="font-weight-bold text-dark mb-1 d-flex align-items-center">
                <FontAwesomeIcon
                  icon={faMapMarkedAlt}
                  className="text-primary mr-3"
                />
                Distribución Geográfica de Estudiantes
              </h4>
              <p className="text-muted mb-0 small">
                Métricas demográficas por país y distribución provincial en Argentina
              </p>
            </div>
            <div className="mt-3 mt-md-0 d-flex gap-2">
              <Button
                variant="outline-success"
                className="d-flex align-items-center shadow-sm font-weight-bold"
                onClick={() => setShowExcelModal(true)}
              >
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                Exportar Excel
              </Button>
            </div>
          </div>
        </Col>

        {/* Metric Cards */}
        <Col lg={3} sm={6} className="mb-3">
          <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
            <Card.Body className="d-flex align-items-center p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: '52px',
                  height: '52px',
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                }}
              >
                <FontAwesomeIcon icon={faUsers} size="lg" />
              </div>
              <div>
                <span className="text-muted small text-uppercase font-weight-bold">
                  Estudiantes Argentina
                </span>
                <h3 className="font-weight-bold mb-0 text-dark">
                  {totalStudentsInArgentina.toLocaleString()}
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} sm={6} className="mb-3">
          <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
            <Card.Body className="d-flex align-items-center p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: '52px',
                  height: '52px',
                  backgroundColor: '#ECFDF5',
                  color: '#059669',
                }}
              >
                <FontAwesomeIcon icon={faCity} size="lg" />
              </div>
              <div>
                <span className="text-muted small text-uppercase font-weight-bold">
                  Provincias Activas
                </span>
                <h3 className="font-weight-bold mb-0 text-dark">
                  {totalProvincesActive}
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} sm={6} className="mb-3">
          <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
            <Card.Body className="d-flex align-items-center p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: '52px',
                  height: '52px',
                  backgroundColor: '#FEF3C7',
                  color: '#D97706',
                }}
              >
                <FontAwesomeIcon icon={faChartBar} size="lg" />
              </div>
              <div>
                <span className="text-muted small text-uppercase font-weight-bold">
                  Provincia Principal
                </span>
                <h5 className="font-weight-bold mb-0 text-dark text-truncate" style={{ maxWidth: '160px' }}>
                  {topProvinceSummary.name}
                </h5>
                <small className="text-muted font-weight-bold">
                  {topProvinceSummary.value} estudiantes
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} sm={6} className="mb-3">
          <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
            <Card.Body className="d-flex align-items-center p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: '52px',
                  height: '52px',
                  backgroundColor: '#F3E8FF',
                  color: '#7C3AED',
                }}
              >
                <FontAwesomeIcon icon={faGlobeAmericas} size="lg" />
              </div>
              <div>
                <span className="text-muted small text-uppercase font-weight-bold">
                  Países Registrados
                </span>
                <h3 className="font-weight-bold mb-0 text-dark">
                  {totalCountriesCount}
                </h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Controls & Filter Panel */}
      <Card className="border-0 shadow-sm rounded-lg mb-4 bg-white">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            {/* View Selector Tabs / ButtonGroup */}
            <Col lg={5} md={12} className="mb-3 mb-lg-0">
              <span className="text-muted small text-uppercase font-weight-bold d-block mb-2">
                <FontAwesomeIcon icon={faLayerGroup} className="mr-1" />
                Modo de Visualización
              </span>
              <ButtonGroup className="w-100 shadow-sm">
                <Button
                  variant={selectedViewMode === 'onlyCities' ? 'primary' : 'outline-primary'}
                  onClick={() => setSelectedViewMode('onlyCities')}
                  className="font-weight-bold py-2"
                >
                  <FontAwesomeIcon icon={faCity} className="mr-2" />
                  Provincias Argentinas
                </Button>
                <Button
                  variant={selectedViewMode === 'citiesAndCountries' ? 'primary' : 'outline-primary'}
                  onClick={() => setSelectedViewMode('citiesAndCountries')}
                  className="font-weight-bold py-2"
                >
                  <FontAwesomeIcon icon={faGlobeAmericas} className="mr-2" />
                  Países y Ciudades
                </Button>
              </ButtonGroup>
            </Col>

            {/* Date Range Period Filter */}
            <Col lg={7} md={12}>
              <div className="p-3 bg-light rounded-lg border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Check
                    type="switch"
                    id="period-filter-switch"
                    label={
                      <span className="font-weight-bold text-dark small">
                        <FontAwesomeIcon icon={faFilter} className="mr-1 text-primary" />
                        Filtrar por período de alta (Provincias)
                      </span>
                    }
                    checked={isPeriodChecked}
                    onChange={handlePeriodToggle}
                  />
                  {isPeriodChecked && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-muted small text-decoration-none"
                      onClick={handleResetFilters}
                    >
                      <FontAwesomeIcon icon={faRedoAlt} className="mr-1" />
                      Restablecer
                    </Button>
                  )}
                </div>

                <Row>
                  <Col sm={6} className="mb-2 mb-sm-0">
                    <InputGroup size="sm">
                      <InputGroup.Prepend>
                        <InputGroup.Text className="bg-white border-right-0 text-muted">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          Desde
                        </InputGroup.Text>
                      </InputGroup.Prepend>
                      <Form.Control
                        type="date"
                        value={selectedInitialDate}
                        onChange={(event) => setSelectedInitialDate(event.target.value)}
                        disabled={!isPeriodChecked}
                        className="border-left-0"
                      />
                    </InputGroup>
                  </Col>
                  <Col sm={6}>
                    <InputGroup size="sm">
                      <InputGroup.Prepend>
                        <InputGroup.Text className="bg-white border-right-0 text-muted">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          Hasta
                        </InputGroup.Text>
                      </InputGroup.Prepend>
                      <Form.Control
                        type="date"
                        value={selectedFinalDate}
                        onChange={(event) => setSelectedFinalDate(event.target.value)}
                        disabled={!isPeriodChecked}
                        className="border-left-0"
                      />
                    </InputGroup>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Chart Visualization */}
      <Card className="border-0 shadow-sm rounded-lg bg-white">
        <Card.Header className="bg-white border-bottom py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <div>
            <h5 className="font-weight-bold text-dark mb-0">
              {selectedViewMode === 'onlyCities'
                ? 'Distribución por Provincias de Argentina'
                : 'Distribución Comparativa por Países y Regiones'}
            </h5>
            <small className="text-muted">
              {selectedViewMode === 'onlyCities'
                ? isPeriodChecked
                  ? `Mostrando registros desde ${selectedInitialDate} hasta ${selectedFinalDate}`
                  : 'Mostrando el total histórico acumulado de estudiantes'
                : 'Visualización detallada de regiones agrupadas por país'}
            </small>
          </div>

          {selectedViewMode === 'onlyCities' && (
            <div className="mt-2 mt-md-0 d-flex align-items-center">
              <span className="text-muted small mr-2 font-weight-bold">Mostrar:</span>
              <ButtonGroup size="sm">
                <Button
                  variant={displayLimit === 'top10' ? 'primary' : 'outline-secondary'}
                  onClick={() => setDisplayLimit('top10')}
                >
                  Top 10
                </Button>
                <Button
                  variant={displayLimit === 'all' ? 'primary' : 'outline-secondary'}
                  onClick={() => setDisplayLimit('all')}
                >
                  Todas ({argentinaProvincesData.length})
                </Button>
              </ButtonGroup>
            </div>
          )}
        </Card.Header>

        <Card.Body className="p-4">
          {selectedViewMode === 'onlyCities' ? (
            sortedProvinces.length > 0 ? (
              <div style={{ width: '100%', height: '480px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedProvinces}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={65}
                    />
                    <YAxis
                      tick={{ fill: '#64748B', fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomChartTooltip />} />
                    <Bar
                      dataKey="value"
                      name="Estudiantes"
                      radius={[8, 8, 0, 0]}
                      barSize={displayLimit === 'top10' ? 44 : 26}
                    >
                      {sortedProvinces.map((provinceEntry, entryIndex) => (
                        <Cell
                          key={`province-cell-${entryIndex}`}
                          fill={MODERN_CHART_COLORS[entryIndex % MODERN_CHART_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <FontAwesomeIcon icon={faCity} size="3x" className="mb-3 text-secondary opacity-50" />
                <h5>No se encontraron registros para el período seleccionado</h5>
                <p className="small mb-3">Intente ampliar el rango de fechas en los filtros superiores.</p>
                <Button variant="outline-primary" size="sm" onClick={handleResetFilters}>
                  Restablecer fechas
                </Button>
              </div>
            )
          ) : (
            <div style={{ width: '100%', height: '520px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stackedCountryData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 40,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="country"
                    tick={{ fill: '#64748B', fontSize: 13, fontWeight: 600 }}
                  />
                  <YAxis
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />
                  {regionCategories?.map((categoryName, categoryIndex) => (
                    <Bar
                      key={`stacked-bar-${categoryName}`}
                      dataKey={categoryName}
                      stackId="stackedGroup"
                      fill={MODERN_CHART_COLORS[categoryIndex % MODERN_CHART_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
